import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Download, Printer, Plus, Check, X, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import { countWord } from '../lib/countWord.mjs';
import { easternDay } from '../../modules/q2c/eastern-day.mjs';
import { Chip, usd, fmtDate, inputCls, downloadFile, openFile } from '../components/bids/FieldGrid';

// Weekly Concria Meet pack (live data), quarterly sales vs the minimum target, and the
// read-only pipeline export for Concria. Everything here is generate-and-download; the
// portal never sends it.

const RAG_TONE = { red: 'red', amber: 'amber', green: 'green', done: 'gray', unknown: 'gray' };
const TABS = [['pack', 'Weekly pack'], ['kpi', 'Quarterly KPI'], ['export', 'Export for Concria']];

export default function ConcriaMeetPage() {
  const [tab, setTab] = useState('pack');
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><CalendarCheck size={20} /> Concria Meet</h1>
        <p className="text-sm text-[var(--text-secondary)]">The Tuesday meeting pack, built from live portal data. Download or print it; nothing is sent from here.</p>
      </div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-[var(--border)]">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-primary font-semibold text-[var(--text-primary)]' : 'border-transparent text-[var(--text-secondary)]'}`}>{l}</button>
        ))}
      </div>
      {tab === 'pack' && <PackTab />}
      {tab === 'kpi' && <KpiTab />}
      {tab === 'export' && <ExportTab />}
    </div>
  );
}

const btn = 'inline-flex items-center gap-1 rounded-lg border border-[var(--border)] bg-white px-3 py-1.5 text-sm hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800';

function arLabel(bk) {
  const ar = bk.ar;
  if (!ar || !ar.source) return 'Customer A/R';
  if (ar.invoices_cents && ar.books_cents) return `Customer A/R (from invoices ${usd(ar.invoices_cents)}, from books ${usd(ar.books_cents)})`;
  return `Customer A/R (${ar.source})`;
}

function Section({ title, children, note }) {
  return (
    <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      <h2 className="mb-2 font-semibold text-[var(--text-primary)]">{title}</h2>
      {note && <p className="mb-2 text-xs text-[var(--text-secondary)]">{note}</p>}
      {children}
    </section>
  );
}
const Unavailable = ({ s }) => <p className="text-sm text-[var(--text-secondary)]">{s.note || 'Not available.'}</p>;

function Table({ head, rows, empty = 'Nothing to show.' }) {
  if (!rows.length) return <p className="text-sm text-[var(--text-secondary)]">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-[var(--text-secondary)]"><tr>{head.map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[var(--border)] align-top">{r.map((c, j) => <td key={j} className="px-2 py-1.5">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function PackTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setData((await api.get('/concria-meet/pack')).pack); setErr(''); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm text-[var(--text-secondary)]">Building pack…</p>;
  const p = data;
  // Clear a previous action failure when a new action starts.
  const run = (fn) => { setErr(''); return fn().catch((e) => setErr(e.message)); };
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <button className={btn} onClick={() => run(() => openFile('/concria-meet/pack.html', { method: 'POST' }))}><Printer size={14} /> Print (partner copy)</button>
        <button className={btn} onClick={() => run(() => downloadFile('/concria-meet/pack.html', { method: 'POST', fallbackName: 'concria-meet-pack.html' }))}><Download size={14} /> Download partner copy</button>
        <button className={btn} onClick={() => run(() => downloadFile('/concria-meet/pack.html?audience=concria', { method: 'POST', fallbackName: 'concria-meet-pack-concria.html' }))}><Download size={14} /> Download Concria copy</button>
        <button className={btn} onClick={load}><RotateCcw size={14} /> Refresh</button>
      </div>
      <p className="mb-3 text-xs text-[var(--text-secondary)]">Week of {fmtDate(p.generated_on)}. The Concria copy leaves out partner investments, cash balances, quote values and internal tasks.</p>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}

      <Section title="Pipeline and bids">
        {p.bids.available ? (<>
          <Table head={['Bid', 'Due', 'Bidding', 'Spec', 'Material quote', 'Status']} empty="No open bids." rows={p.bids.open.map((b) => [
            <span key="t">{b.title}{b.federal && <> <Chip tone="indigo">federal</Chip></>}{b.long_lead && <> <Chip tone="indigo">long lead</Chip></>}</span>,
            b.due ? `${fmtDate(b.due)}${b.days_to_due != null ? (b.days_to_due < 0 ? ` (${-b.days_to_due} d ago)` : ` (in ${b.days_to_due} d)`) : ''}` : 'Not set',
            [...b.gcs, ...b.subs].join(', '),
            b.spec_label,
            [b.quote_ref, b.quote_date && fmtDate(b.quote_date), b.quote_amount_cents != null && usd(b.quote_amount_cents)].filter(Boolean).join(', ') || (b.quote_sent === false ? 'Not sent' : ''),
            b.outcome_label,
          ])} />
          {!!p.bids.decided_recently.length && (<>
            <h3 className="mt-3 text-sm font-semibold">Decided in the last 30 days</h3>
            <Table head={['Bid', 'Outcome', 'Date']} rows={p.bids.decided_recently.map((b) => [b.title, b.outcome_label, fmtDate(b.outcome_date)])} />
          </>)}
        </>) : <Unavailable s={p.bids} />}
        {p.leads.available && (
          <p className="mt-3 text-sm">Open leads: <strong>{p.leads.count}</strong> {Object.entries(p.leads.by_stage).map(([k, v]) => <Chip key={k}>{k}: {v}</Chip>)} <Link to="/leads" className="ml-1 text-xs underline">Leads</Link></p>
        )}
      </Section>

      <Section title="Shipments">
        {p.shipments.available ? (
          <Table head={['Order', 'Customer / project', 'Milestone', 'Need by', 'Projected delivery', 'Slack', 'Status']} empty="No active shipments." rows={p.shipments.rows.map((s) => [
            <Link key="o" to={`/shipments/${s.id}`} className="underline">{s.order_ref}</Link>,
            [s.customer, s.project].filter(Boolean).join(': '),
            s.milestone || '',
            fmtDate(s.need_by),
            <span key="p">{fmtDate(s.projected_delivery)}{s.projected_delivery_conservative && s.projected_delivery_conservative !== s.projected_delivery && <span className="block text-xs text-[var(--text-secondary)]">worst normal {fmtDate(s.projected_delivery_conservative)}</span>}</span>,
            s.slack_days != null ? `${s.slack_days} d` : '',
            <Chip key="r" tone={RAG_TONE[s.rag]}>{s.rag_label}</Chip>,
          ])} />
        ) : <Unavailable s={p.shipments} />}
      </Section>

      <Section title="Open money items">
        {p.money.concria_oy.available ? (<>
          <h3 className="text-sm font-semibold">Concria Oy ledger</h3>
          {p.money.concria_oy.net_label && <p className="text-sm font-medium">{p.money.concria_oy.net_label}</p>}
          <Table head={['Item', 'Amount', 'Note']} rows={(p.money.concria_oy.lines || []).map((l) => [l.label, l.amount_display || usd(l.amount_cents), l.note || ''])} />
        </>) : <p className="text-sm text-[var(--text-secondary)]">Concria Oy ledger: {p.money.concria_oy.note}</p>}
        {p.money.books.available ? (
          <div className="mt-2">
            <Table head={['Books', 'Amount']} rows={[
              ['Due from Concria', usd(p.money.books.due_from_concria_cents)], [arLabel(p.money.books), usd(p.money.books.ar_cents)], ['Bills owed (A/P)', usd(p.money.books.ap_cents)],
              ...(p.money.books.cash || []).map((c) => [`Cash: ${c.name}`, usd(c.balance_cents)]),
            ]} />
            {p.money.books.empty && <p className="text-xs text-[var(--text-secondary)]">Books has no posted entries yet. Customer A/R comes from invoices; the other lines read zero.</p>}
          </div>
        ) : <p className="text-sm text-[var(--text-secondary)]">Books: {p.money.books.note}</p>}
        {p.money.investments.available ? (
          <div className="mt-2">
            <Table head={['Partner investments', 'Still owed', 'Needs confirmation']} rows={p.money.investments.summary.map((m) => [m.member, usd(m.owed_cents), usd(m.needs_confirmation_cents)])} />
          </div>
        ) : <p className="text-sm text-[var(--text-secondary)]">Partner investments: {p.money.investments.note}</p>}
      </Section>

      <Section title="Open tasks">
        {p.tasks.available ? (
          <Table head={['Task', 'About', 'Owner', 'Due', 'Status']} empty="No open tasks." rows={p.tasks.rows.map((t) => [
            t.title, t.about || '', t.owner || '', <span key="d">{fmtDate(t.due)}{t.overdue && <> <Chip tone="red">overdue</Chip></>}</span>, t.status,
          ])} />
        ) : <Unavailable s={p.tasks} />}
      </Section>

      <Section title="Agenda carry-over" note="Open items carry into every pack until closed.">
        {p.notes.available ? <Notes notes={p.notes} onChange={load} /> : <Unavailable s={p.notes} />}
      </Section>
    </div>
  );
}

function Notes({ notes, onChange }) {
  const [topic, setTopic] = useState('');
  const [note, setNote] = useState('');
  const [owner, setOwner] = useState('');
  const [err, setErr] = useState('');
  async function add() {
    if (!topic.trim()) { setErr('Topic is required'); return; }
    try {
      await api.post('/concria-meet/notes', { topic, note: note || null, owner: owner || null, meeting_date: easternDay(new Date()) });
      setTopic(''); setNote(''); setOwner(''); setErr(''); onChange();
    } catch (e) { setErr(e.message); }
  }
  const setStatus = (n, status) => { setErr(''); return api.patch(`/concria-meet/notes/${n.id}`, { status }).then(onChange).catch((e) => setErr(e.message)); };
  return (
    <div>
      <ul className="space-y-1">
        {notes.carry_over.map((n) => (
          <li key={n.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-[var(--border)] p-2 text-sm">
            <div className="min-w-0">
              <div className="font-medium">{n.topic}{n.owner && <span className="ml-1 text-xs text-[var(--text-secondary)]">({n.owner})</span>}</div>
              {n.note && <div className="text-xs text-[var(--text-secondary)]">{n.note}</div>}
              {n.meeting_date && <div className="text-xs text-[var(--text-secondary)]">Raised {fmtDate(n.meeting_date)}</div>}
            </div>
            <div className="flex gap-1">
              <button className={btn} onClick={() => setStatus(n, 'done')}><Check size={12} /> Done</button>
              <button className={btn} onClick={() => setStatus(n, 'dropped')}><X size={12} /> Drop</button>
            </div>
          </li>
        ))}
        {!notes.carry_over.length && <li className="text-sm text-[var(--text-secondary)]">No open agenda items.</li>}
      </ul>
      {!!notes.closed_recently.length && (
        <p className="mt-2 text-xs text-[var(--text-secondary)]">Closed in the last two weeks: {notes.closed_recently.map((n) => `${n.topic} (${n.status})`).join('; ')}</p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr_auto_auto]">
        <input className={inputCls} placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
        <input className={inputCls} placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <select className={inputCls} value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Owner">
          <option value="">Owner</option>{['Chad', 'Steve', 'Chase', 'Concria'].map((o) => <option key={o}>{o}</option>)}
        </select>
        <button className={btn} onClick={add}><Plus size={14} /> Add</button>
      </div>
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
    </div>
  );
}

function KpiTab() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [kpi, setKpi] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    // Ignore responses for a year that is no longer selected.
    let current = true;
    setKpi(null);
    setErr('');
    api.get(`/concria-meet/kpi?year=${year}`).then((r) => { if (!current) return; setKpi(r.kpi); setErr(''); }).catch((e) => { if (!current) return; setErr(e.message); });
    return () => { current = false; };
  }, [year]);
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className={`${inputCls} w-auto`} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">
          {[thisYear - 2, thisYear - 1, thisYear].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!kpi && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {kpi && (
        <Section title={`Sales against the ${usd(kpi.target_cents)} minimum target`} note={kpi.target_note}>
          {kpi.sales_note && <p className="mb-2 rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">{kpi.sales_note}</p>}
          {kpi.ytd_sales_cents != null && (
            <div className="mb-3">
              <div className="mb-1 text-sm">Year to date: <strong>{usd(kpi.ytd_sales_cents)}</strong> invoiced{kpi.ytd_sales_source ? ` (${kpi.ytd_sales_source})` : ''}, {kpi.pct_of_target}% of target{kpi.ytd_collected_cents != null ? `; ${usd(kpi.ytd_collected_cents)} collected` : ''}</div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700" role="progressbar" aria-valuenow={kpi.pct_of_target} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.min(100, kpi.pct_of_target || 0)}%` }} />
              </div>
            </div>
          )}
          <Table head={['Quarter', 'Sales', 'Collected', 'Cumulative', 'Pace to target', 'Bids won', 'Quotes sent']} rows={kpi.quarters.map((q) => [
            q.label,
            q.sales_cents == null ? (q.started ? '' : 'not started') : <span key="s">{usd(q.sales_cents)}{q.sales_source && <span className="block text-xs text-[var(--text-secondary)]">{q.sales_source}</span>}</span>,
            q.collected_cents == null ? '' : <span key="c">{usd(q.collected_cents)}{q.collected_source && <span className="block text-xs text-[var(--text-secondary)]">{q.collected_source}</span>}</span>,
            q.cumulative_cents == null ? '' : usd(q.cumulative_cents),
            usd(q.pace_target_cents), `${q.bids_won}${q.bids_won_value_cents ? ` (${usd(q.bids_won_value_cents)})` : ''}`,
            `${q.quotes_sent}${q.quotes_sent_value_cents ? ` (${usd(q.quotes_sent_value_cents)})` : ''}`,
          ])} />
          <p className="mt-2 text-xs text-[var(--text-secondary)]">{kpi.sales_basis_note || 'Sales = income posted in Books for the quarter.'} Bids won and quotes sent come from Bids &amp; Specs (quote value where recorded).</p>
        </Section>
      )}
    </div>
  );
}

function ExportTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [leadIds, setLeadIds] = useState(null);
  const [bidIds, setBidIds] = useState(null);
  const [values, setValues] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/concria-meet/export/preview').then((r) => {
      setData(r); setLeadIds(new Set(r.leads.map((l) => l.id))); setBidIds(new Set(r.bids.map((b) => b.id)));
    }).catch((e) => setErr(e.message));
  }, []);
  const toggle = (set, setter, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setter(n); };
  const counts = useMemo(() => (leadIds && bidIds ? `${leadIds.size} ${countWord(leadIds.size, 'lead')}, ${bidIds.size} ${countWord(bidIds.size, 'bid')}` : ''), [leadIds, bidIds]);
  async function generate(format) {
    setMsg('');
    setErr('');
    try {
      await downloadFile('/concria-meet/export', { method: 'POST', body: { format, lead_ids: [...leadIds], bid_ids: [...bidIds], include_values: values }, fallbackName: `ccs-pipeline-for-concria.${format}` });
      setMsg('File downloaded. Review it before sharing; the portal does not send it.');
    } catch (e) { setErr(e.message); }
  }
  if (!data) return err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm text-[var(--text-secondary)]">Loading…</p>;
  return (
    <div>
      <Section title="Read-only pipeline export for Concria" note="Pick the leads and bids to include, then download. Concria never gets a login; share the file yourself if you decide to.">
        {data.notes.map((n) => <p key={n} className="text-xs text-amber-700">{n}</p>)}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-1 text-sm font-semibold">Leads</h3>
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-[var(--border)] p-2 text-sm">
              {data.leads.map((l) => (
                <label key={l.id} className="flex items-center gap-2"><input type="checkbox" checked={leadIds.has(l.id)} onChange={() => toggle(leadIds, setLeadIds, l.id)} />
                  <span className="min-w-0 flex-1 truncate">{[l.company, l.name].filter(Boolean).join(': ')}</span><span className="text-xs text-[var(--text-secondary)]">{l.stage}</span></label>
              ))}
              {!data.leads.length && <p className="text-[var(--text-secondary)]">No open leads.</p>}
            </div>
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">Bids</h3>
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-[var(--border)] p-2 text-sm">
              {data.bids.map((b) => (
                <label key={b.id} className="flex items-center gap-2"><input type="checkbox" checked={bidIds.has(b.id)} onChange={() => toggle(bidIds, setBidIds, b.id)} />
                  <span className="min-w-0 flex-1 truncate">{b.title}</span><span className="text-xs text-[var(--text-secondary)]">{b.outcome || 'open'}</span></label>
              ))}
              {!data.bids.length && <p className="text-[var(--text-secondary)]">No bids.</p>}
            </div>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={values} onChange={(e) => setValues(e.target.checked)} /> Include quote numbers and values</label>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button className={btn} onClick={() => generate('csv')}><Download size={14} /> Download CSV</button>
          <button className={btn} onClick={() => generate('html')}><Download size={14} /> Download HTML</button>
          <span className="text-xs text-[var(--text-secondary)]">{counts}</span>
        </div>
        {msg && <p className="mt-2 text-sm text-emerald-700">{msg}</p>}
        {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      </Section>
    </div>
  );
}
